import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useI18n } from "@core/i18n/i18n";
import { userManager } from "@core/service-container";
import { NotAuthorizedCodeError } from "@domain/authentication/authentication-errors";
import styled from "@emotion/styled";
import { Button } from "@ui/components/buttons/button";
import { BaseDialog, DIALOG_SIZE } from "@ui/components/dialog/base-dialog";
import { ErrorMessage } from "@ui/components/common/error-message";
import { PasswordInput } from "@ui/components/inputs/text-input";
import { ToastContext } from "@ui/components/common/toast-provider";
import { PasswordField } from "@ui/pages/login/components/password-field";
import { sendAnalyticsEventSavePasswordAccount } from "@ui/pages/settings/settings-analytics";
import { row, stack } from "@ui/style";
import { motion } from "framer-motion";
import { DialogProps } from "primereact/dialog";
import React, { useCallback, useContext } from "react";
import { Controller, useForm } from "react-hook-form";

export type EditPasswordDialogProps = DialogProps;

interface FormData {
	currentPassword: string;
	newPassword: string;
}

export const EditPasswordDialog = ({ ...otherProps }: EditPasswordDialogProps): JSX.Element => {
	const { format } = useI18n();
	const { showToast } = useContext(ToastContext);
	const { control, handleSubmit, setError } = useForm<FormData>({
		defaultValues: { currentPassword: "", newPassword: "" },
	});

	const [submitNewPassword, isLoading] = useAsyncOperation(
		useCallback(
			async ({ currentPassword, newPassword }: FormData) => {
				try {
					await userManager.changePassword(currentPassword, newPassword);
					sendAnalyticsEventSavePasswordAccount();
					showToast("", format("settings.accountTab.editPasswordDialog.successToast.detail"), "success");
					otherProps.onHide();
				} catch (e) {
					if (e instanceof NotAuthorizedCodeError) {
						setError("currentPassword", {
							message: format("settings.accountTab.editPasswordDialog.currentPassword.invalidError"),
						});
					}
					console.error("Failure to change password", e);
				}
			},
			[format, otherProps, setError, showToast],
		),
		{},
	);

	return (
		<BaseDialog
			closeOnEscape={false}
			header={format("settings.accountTab.editPasswordDialog.header")}
			footer={
				<ButtonContainer>
					<Button
						loading={isLoading}
						type="submit"
						label={format("global.dialog.save")}
						onClick={handleSubmit(submitNewPassword)}
					/>
				</ButtonContainer>
			}
			width={DIALOG_SIZE.S}
			{...otherProps}
		>
			<Form onSubmit={handleSubmit(submitNewPassword)}>
				<ControllersContainer>
					<Controller
						name="currentPassword"
						control={control}
						rules={{
							required: format("global.form.field.requiredError"),
						}}
						render={({ field, fieldState }) => (
							<>
								<label className="p-size-m">
									{format("settings.accountTab.editPasswordDialog.currentPassword.label")}
								</label>
								<PasswordInput
									{...field}
									placeholder={format("settings.accountTab.editPasswordDialog.currentPassword.placeholder")}
									error={fieldState.invalid}
								/>
								<ErrorMessage>{fieldState.error?.message}</ErrorMessage>
							</>
						)}
					/>

					<FormField>
						<PasswordField
							control={control}
							error={false}
							placeholder={format("settings.accountTab.editPasswordDialog.newPassword.placeholder")}
							name="newPassword"
							label={format("settings.accountTab.editPasswordDialog.newPassword.label")}
							labelStyle={{ marginTop: "16px" }}
							rulesStyle={{ fontSize: "0.75rem", marginTop: 8 }}
						/>
					</FormField>
				</ControllersContainer>
			</Form>
		</BaseDialog>
	);
};

const Form = styled(motion.form)`
	${stack("M", "spacebetween", "stretch")};
	height: 100%;
`;

const ControllersContainer = styled.div`
	${stack("M", "flexstart", "stretch")};
`;

const FormField = styled(motion.div)`
	${stack("S", "flexstart", "stretch")};
`;

const ButtonContainer = styled.div`
	${row(24, "flexend")}
`;
